/*******************************************************
* Theme Configuration
* 
* Material-UI theme configuration that implements TRUE 2 design tokens.
* Manages light/dark mode palettes and consistent design patterns across the application.
* 
* Key Features:
* - Dynamic theme switching between light/dark modes
* - Multiple brand theme support (TRUE2, LSU, AM, UT, AL)
* - Custom breakpoint configuration
* - The design rails (tokens.ts) for both looks: type scale, motion, status
*   colors, grey scale and control sizes. New-look-only surface choices
*   (outlined panels, flat app bar, input and sheet shapes) live in newLookTheme.ts
* 
* Primary Operations:
* - getDesignTokens(): Generates theme options based on mode and theme name
* - getAvailableThemes(): Returns list of available theme names
* 
* Usage:
* ```typescript
* // Basic usage example
* import { getDesignTokens, getAvailableThemes } from './theme';
* 
* // Example 1: Get light mode TRUE2 theme
* const lightTheme = getDesignTokens('light', 'TRUE2');
* 
* // Example 2: Get available themes
* const themes = getAvailableThemes();
* ```
********************************************************/

//########## IMPORTS ##########
//#region IMPORTS
import { alpha, type PaletteMode, type ThemeOptions } from '@mui/material/styles';
import true2Theme from './ThemeTRUE2.json';
import lsuTheme from './ThemeLSU.json';
import AMTheme from './ThemeAM.json';
import UTTheme from './ThemeUT.json';
import ALTheme from './ThemeAL.json';
import { tokens as t, customTypeVariants, rem, brandOf } from './tokens';
//#endregion


//########## TYPE DEFINITIONS ##########
//#region TYPE DEFINITIONS
// Create a themes object that maps theme names to their JSON data
type ThemeData = {
  schemes: {
    light: Record<string, string>;
    dark: Record<string, string>;
  };
  palettes: {
    primary: Record<string | number, string>;
    secondary: Record<string | number, string>;
    neutral: Record<string | number, string>;
    'neutral-variant': Record<string | number, string>;
    tertiary: Record<number, string>;
  };
};

// Extend the default breakpoints interface
declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: false;
    sm: false;
    md: false;
    lg: false;
    xl: false;
    phone: true;
    phoneBig: true;
    tablet: true;
    halfScreen: true;
    fullScreen: true;
  }

  // Status text role: the readable tone for bare text and thin icons.
  // Set for success, warning, info and error; primary and secondary don't have one.
  interface PaletteColor {
    text?: string;
  }
  // The tenant's own color, for filled surfaces: app bar, contained buttons, nodes, tiles.
  // primary is the accent for text, icons and outlines; in a dark scheme it's a light tint of
  // the brand, because a school color can't be read as text on a dark page.
  // Optional because MUI's default theme doesn't have it: read it with brandOf()
  interface Palette {
    brand?: { main: string; contrastText: string; avatarFrom: string; avatarTo: string };
  }
  interface PaletteOptions {
    brand?: { main: string; contrastText: string; avatarFrom: string; avatarTo: string };
  }
  interface SimplePaletteColorOptions {
    text?: string;
  }

  // Rails type variants (tokens.ts)
  interface TypographyVariants {
    micro: React.CSSProperties;
    tag: React.CSSProperties;
    bodySmall: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    micro?: React.CSSProperties;
    tag?: React.CSSProperties;
    bodySmall?: React.CSSProperties;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsSizeOverrides {
    compact: true;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    micro: true;
    tag: true;
    bodySmall: true;
  }
}
//#endregion


//########## CONSTANTS & Global Variables ##########
//#region CONSTANTS
const themes: Record<string, ThemeData> = {
  'TRUE2': true2Theme,
  'A&M': AMTheme,
  'Alabama': ALTheme,
  'LSU': lsuTheme,
  'UT': UTTheme
};

/** Numbers are the product: columns of quantities and hours must line up */
const TABULAR_NUMS = { fontVariantNumeric: 'tabular-nums' } as const;

/** Contained colors that get a state-layer hover instead of jumping to palette.dark */
const CONTAINED_COLORS = ['primary', 'secondary', 'success', 'warning', 'error', 'info'] as const;
type ContainedColor = typeof CONTAINED_COLORS[number];
const isContainedColor = (color: unknown): color is ContainedColor => CONTAINED_COLORS.includes(color as ContainedColor);
//#endregion


//########## FUNCTIONS ##########
//#region FUNCTIONS
/** The Material Theme Builder data for a theme (falls back to TRUE2) */
const getThemeData = (themeName = 'TRUE2'): ThemeData => themes[themeName] || themes.TRUE2;

/** The type scale; every step comes from tokens.fontSize */
const typography: ThemeOptions['typography'] = {
  ...customTypeVariants,
  h3: { fontSize: rem(t.fontSize.display), fontWeight: t.fontWeight.heavy, lineHeight: t.lineHeight.tight, ...TABULAR_NUMS },
  h4: { fontSize: rem(t.fontSize.hero), fontWeight: t.fontWeight.heavy, lineHeight: t.lineHeight.tight },
  h5: { fontSize: rem(t.fontSize.page), fontWeight: t.fontWeight.semibold, lineHeight: t.lineHeight.tight },
  h6: { fontSize: rem(t.fontSize.title), fontWeight: t.fontWeight.semibold, lineHeight: t.lineHeight.tight },
  subtitle1: { fontSize: rem(t.fontSize.large), fontWeight: t.fontWeight.medium },
  subtitle2: { fontSize: rem(t.fontSize.body), fontWeight: t.fontWeight.medium },
  body1: { fontSize: rem(t.fontSize.large), ...TABULAR_NUMS },
  body2: { fontSize: rem(t.fontSize.body), ...TABULAR_NUMS },
  caption: { fontSize: rem(t.fontSize.caption), ...TABULAR_NUMS },
  overline: {
    fontSize: rem(t.fontSize.tag),
    fontWeight: t.fontWeight.bold,
    letterSpacing: t.letterSpacing.caps,
    lineHeight: t.lineHeight.snug,
  },
};

/**
 * Height, padding and label size for one button size (tokens.button).
 * theme is typed by what it uses: importing MUI's Theme into this file sends sx types elsewhere past TS's union limit (TS2590).
 */
const buttonSizeStyle = (size: keyof typeof t.button, theme: { spacing: (y: number, x: number) => string }, variant?: string) => {
  const { height, paddingY, paddingX, textPaddingX, fontSize } = t.button[size];
  return {
    minHeight: height,
    padding: theme.spacing(paddingY, variant === 'text' ? textPaddingX : paddingX),
    fontSize: rem(fontSize),
    lineHeight: t.lineHeight.normal,
  };
};

/** Grey runs light to dark like MUI's scale (MUI's own components rely on that order) */
const getGrey = (palettes: ThemeData['palettes']) => {
  const n = palettes.neutral;
  const nv = palettes['neutral-variant'];
  return {
    50: n[98], 100: n[95], 200: n[90], 300: n[80], 400: n[70],
    500: n[60], 600: n[50], 700: n[40], 800: n[30], 900: n[10],
    A100: nv[95], A200: nv[90], A400: nv[70], A700: nv[40],
  };
};

const getDesignTokens = (mode: PaletteMode, themeName = 'TRUE2'): ThemeOptions => {
  // Get the theme JSON or fallback to TRUE2 theme
  const themeJson = getThemeData(themeName);
  const scheme = themeJson.schemes[mode];
  const palettes = themeJson.palettes;

  return {
    breakpoints: {
      values: {
        phone: 0,      // Small phones
        phoneBig: 400,    // Phones
        tablet: 768,    // Tablets
        halfScreen: 960,   // Half screen of PC
        fullScreen: 1200,   // Standard Screens
      },
    },
    transitions: {
      duration: {
        shortest: t.motion.fast,
        shorter: t.motion.base,
        enteringScreen: t.motion.enter,
      },
    },
    typography,
    components: {
      MuiTypography: {
        defaultProps: {
          variantMapping: { micro: 'span', tag: 'span', bodySmall: 'p' },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            overscrollBehavior: 'none',
            colorScheme: mode,
          },
          body: {
            overscrollBehavior: 'none',
            colorScheme: mode,
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          asterisk: {
            color: scheme.error,
          },
        },
      },
      MuiTextField: {
        //always highlight all text with first click (desktop only — mobile select triggers copy/paste popup)
        defaultProps: {
          onFocusCapture: (e: React.FocusEvent<HTMLInputElement>) => {
            if (window.matchMedia('(pointer: coarse)').matches) return;
            if (typeof e.target.select === 'function') {
              e.target.select();
            }
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: ({ theme, ownerState }) => ({
            // compact is our own size, so MUI has no sizeCompact slot for it
            ...(ownerState.size === 'compact' && buttonSizeStyle('compact', theme, ownerState.variant)),
            boxShadow: theme.shadows[4],
            '&:hover': {
              boxShadow: theme.shadows[8],
            },
            '&:disabled': {
              boxShadow: theme.shadows[1],
            },
            '&:active': {
              boxShadow: theme.shadows[2],
            },
          }),
          // MUI hovers a contained button with palette.dark, which in this palette
          // is the on-container text color (near-black navy, or invisible on warning).
          // Use an 8% state layer of the label color over the fill instead.
          contained: ({ theme, ownerState }) => {
            if (!isContainedColor(ownerState.color)) return {};
            // A primary button is a brand surface. Where brand and accent differ (a school color on a
            // dark page), a thin accent border keeps its edge visible against the page.
            const isBrandFill = ownerState.color === 'primary' && brandOf(theme).main !== theme.palette.primary.main;
            const { main, contrastText } = isBrandFill ? brandOf(theme) : theme.palette[ownerState.color];
            const layer = alpha(contrastText, t.tint.fill);
            return {
              ...(isBrandFill && {
                backgroundColor: main,
                color: contrastText,
                border: `${t.border.hairline}px solid ${theme.palette.primary.main}`,
                '&.Mui-disabled': { borderColor: 'transparent' },
              }),
              '&:hover': {
                backgroundColor: main,
                backgroundImage: `linear-gradient(${layer}, ${layer})`,
              },
            };
          },
          // Warning main is a fill; bare labels use the readable text role
          text: ({ theme, ownerState }) => ownerState.color === 'warning' ? { color: theme.palette.warning.text } : {},
          outlined: ({ theme, ownerState }) => ownerState.color === 'warning' ? { color: theme.palette.warning.text } : {},
          sizeSmall: ({ theme, ownerState }) => buttonSizeStyle('small', theme, ownerState.variant),
          sizeMedium: ({ theme, ownerState }) => buttonSizeStyle('medium', theme, ownerState.variant),
          sizeLarge: ({ theme, ownerState }) => ({
            ...buttonSizeStyle('large', theme, ownerState.variant),
            fontWeight: t.fontWeight.bold,
          }),
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: ({ theme }) => ({ padding: theme.spacing(t.space.md, t.space.lg) }),
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: ({ theme }) => ({ padding: theme.spacing(t.space.md, t.space.lg) }),
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          // Side padding lines the buttons up with the content above them
          root: ({ theme }) => ({ padding: theme.spacing(t.space.barY, t.space.lg) }),
        },
      },
      MuiChip: {
        styleOverrides: {
          sizeSmall: { height: t.size.tag },
          iconSmall: { fontSize: t.iconSize.md },
          avatarSmall: { width: t.iconSize.md, height: t.iconSize.md },
          outlined: ({ theme, ownerState }) => ownerState.color === 'warning' ? { color: theme.palette.warning.text } : {},
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: ({ theme, ownerState }) => ownerState.color === 'warning' ? { color: theme.palette.warning.text } : {},
        },
      },
      MuiTooltip: {
        defaultProps: {
          enterDelay: 400,
        },
      },
    },
    palette: {
      mode,
      common: {
        black: palettes.neutral[0],
        white: palettes.neutral[100],
      },
      brand: {
        main: scheme.brand ?? scheme.primary,
        contrastText: scheme.onBrand ?? scheme.onPrimary,
        // Mid tones of the brand: white initials read on them, and they stay the school's hue in both modes
        avatarFrom: palettes.primary[40],
        avatarTo: palettes.primary[60],
      },
      primary: {
        main: scheme.primary,
        light: scheme.primaryContainer,
        dark: scheme.onPrimaryContainer,
        contrastText: scheme.onPrimary,
      },
      secondary: {
        main: scheme.secondary,
        light: scheme.secondaryContainer,
        dark: scheme.onSecondaryContainer,
        contrastText: scheme.onSecondary,
      },
      error: {
        main: scheme.error,
        light: scheme.errorContainer,
        dark: scheme.onErrorContainer,
        contrastText: scheme.onError,
        text: scheme.error,
      },
      // Fixed, readable status colors: green and amber mean the same thing in every tenant
      ...t.status[mode],
      grey: getGrey(palettes),
      text: {
        primary: scheme.onBackground,
        secondary: scheme.onSurfaceVariant,
        disabled: scheme.onSurface + '61', // 38% opacity as per Material Design
      },
      // The soft hairline; the strong outline is for control borders
      divider: scheme.outlineVariant,
      background: {
        default: scheme.background,
        paper: scheme.surfaceContainer,
      },
    },
  };
};

// Get available theme names
const getAvailableThemes = (): string[] => Object.keys(themes);
//#endregion


//########## EXPORTS ##########
//#region EXPORTS
export type { ThemeData };
export {
  getDesignTokens,
  getThemeData,
  getAvailableThemes
}
//#endregion
